import { Component, input, model, computed, output, signal, effect, OnDestroy, inject } from "@angular/core";
import { DialogModule } from "primeng/dialog";
import { ButtonModule } from "primeng/button";
import { TagModule } from "primeng/tag";
import { DividerModule } from "primeng/divider";
import { CardModule } from "primeng/card";
import { AvatarModule } from "primeng/avatar";
import { ChipModule } from "primeng/chip";
import { ProgressBarModule } from "primeng/progressbar";
import { CommonModule, DatePipe, TitleCasePipe } from "@angular/common";
import { IUser } from "../../../../Utils/_models/users.model";
import { UserStatusModalComponent, StatusActionPayload } from "../user-status-modal/user-status-modal.component";
import { AdminService } from "../../../../Utils/_services/admin/admin/admin.service";
import { ToastrService } from "ngx-toastr";

@Component({
  selector: "app-user-details-modal",
  standalone: true,
  imports: [
    DialogModule,
    ButtonModule,
    TagModule,
    DividerModule,
    CardModule,
    AvatarModule,
    ChipModule,
    ProgressBarModule,
    DatePipe,
    TitleCasePipe,
    UserStatusModalComponent,
    CommonModule
  ],
  templateUrl: "./user-details-modal.component.html",
  styleUrl: "./user-details-modal.component.css",
})
export class UserDetailsModalComponent implements OnDestroy {
  user = input<IUser>();
  visible = model<boolean>(false);

  onClose = output();
  avatarImageFailed = false;
  currentPhotoIndex = signal<number>(0);
  isPlaying = signal<boolean>(false);
  private currentAudio: HTMLAudioElement | null = null;
  private currentSpeech: SpeechSynthesisUtterance | null = null;
  
  isStatusModalVisible = signal(false);
  private adminService = inject(AdminService);
  private toastr = inject(ToastrService);

  ngOnDestroy() {
    this.stopPlayback();
  }

  stopPlayback() {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.currentAudio = null;
    }
    if (this.currentSpeech) {
      window.speechSynthesis.cancel();
      this.currentSpeech = null;
    }
    this.isPlaying.set(false);
  }

  private initPhotoIndex = effect(() => {
    const userData = this.user();
    if (userData) {
      const photos = userData.profilePhotos || [];
      const primaryIndex = photos.findIndex(p => p.isPrimary);
      this.currentPhotoIndex.set(primaryIndex >= 0 ? primaryIndex : 0);
      this.avatarImageFailed = false;

      // Preload all photos in the background for instant navigation
      photos.forEach(photo => {
        if (photo.signedUrl) {
          const img = new Image();
          img.src = photo.signedUrl;
        }
      });
    }
  });

fullName = computed(() => {
  const userData = this.user();

  if (!userData) return 'N/A';

  const firstName = userData.firstName?.trim() ?? '';
  const lastName = userData.lastName?.trim() ?? '';

  const name = `${firstName} ${lastName}`.trim();

  return name || 'N/A';
});
  location = computed(() => {
    const userData = this.user();
    return userData ? `${userData.location?.city}, ${userData.location?.state}, ${userData.location?.country}` : "N/A";
  });

  // netBalance = computed(() => {
  //   const userData = this.user();
  //   if (!userData) return 0;
  //   return (userData.totalEarned || 0) - (userData.totalSpent || 0);
  // });

  getInitials(): string {
    const userData = this.user();
    if (!userData || !userData.firstName) return "N/A";
    return `${userData.firstName[0]}${userData.lastName[0]}`.toUpperCase();
  }


  onAvatarError(event: any): void {
    this.avatarImageFailed = true;
  }

  getProfileImage(): string | undefined {
    if (this.avatarImageFailed) return undefined;
    const photos = this.user()?.profilePhotos || [];
    if (photos.length === 0) return undefined;
    const idx = this.currentPhotoIndex();
    const safeIdx = idx >= 0 && idx < photos.length ? idx : 0;
    return photos[safeIdx]?.signedUrl || undefined;
  }

  prevPhoto(event: MouseEvent): void {
    event.stopPropagation();
    const photos = this.user()?.profilePhotos || [];
    if (photos.length <= 1) return;
    this.avatarImageFailed = false;
    this.currentPhotoIndex.update(idx => (idx - 1 + photos.length) % photos.length);
  }

  nextPhoto(event: MouseEvent): void {
    event.stopPropagation();
    const photos = this.user()?.profilePhotos || [];
    if (photos.length <= 1) return;
    this.avatarImageFailed = false;
    this.currentPhotoIndex.update(idx => (idx + 1) % photos.length);
  }

  openImageInNewTab(event: MouseEvent): void {
    event.stopPropagation();
    const url = this.getProfileImage();
    if (url) {
      window.open(url, "_blank");
    }
  }

  playVoiceRecord(url: string | undefined): void {
    if (this.isPlaying()) {
      this.stopPlayback();
      return;
    }

    this.isPlaying.set(true);

    if (url) {
      this.currentAudio = new Audio(url);
      this.currentAudio.onended = () => {
        this.isPlaying.set(false);
        this.currentAudio = null;
      };
      this.currentAudio.play().catch(err => {
        console.error("Error playing audio", err);
        this.isPlaying.set(false);
      });
    } else {
      const name = this.fullName();
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
        this.currentSpeech = new SpeechSynthesisUtterance(name);
        this.currentSpeech.onend = () => {
          this.isPlaying.set(false);
          this.currentSpeech = null;
        };
        this.currentSpeech.onerror = () => {
          this.isPlaying.set(false);
          this.currentSpeech = null;
        };
        window.speechSynthesis.speak(this.currentSpeech);
      } else {
        this.isPlaying.set(false);
      }
    }
  }

  closeDialog(): void {
    this.stopPlayback();
    this.onClose.emit();
  }

  openStatusModal(user: IUser) {
    this.isStatusModalVisible.set(true);
  }

  closeStatusModal() {
    this.isStatusModalVisible.set(false);
  }

  handleStatusAction(payload: StatusActionPayload) {
    const suspendedUntilStr = payload.suspendDays ? new Date(Date.now() + payload.suspendDays * 24 * 60 * 60 * 1000).toISOString() : undefined;
    
    const mappedAccountStatus = payload.action === 'ban' ? 'banned' : (payload.action === 'suspend' ? 'suspended' : 'active');
    
    this.adminService.updateUserStatus(
      payload.user._id, 
      payload.action === 'active' || payload.action === 'suspend', 
      mappedAccountStatus,
      suspendedUntilStr,
      payload.reason,
      payload.sendEmail,
      payload.emailSubject,
      payload.emailBody,
      payload.suspendedServices
    ).subscribe({
      next: res => {
        if (res.auth.success) {
          // Update the user input locally so the modal reflects the new status
          const updatedUser = this.user();
          if (updatedUser) {
            updatedUser.isActive = payload.action === 'active';
            updatedUser.accountStatus = mappedAccountStatus;
            updatedUser.reason = payload.reason;
            updatedUser.suspendedServices = payload.suspendedServices;
            updatedUser.suspendedUntil = suspendedUntilStr ? new Date(suspendedUntilStr) : undefined;
          }
          this.closeStatusModal();
          this.toastr.success(`User status updated to ${payload.action === 'ban' ? 'banned' : (payload.action === 'suspend' ? 'suspended' : (payload.action === 'deactivate' ? 'inactive' : 'active'))}`);
        } else {
          this.toastr.error('Failed to update user status');
        }
      },
      error: (err) => {
        this.toastr.error(err?.error?.message || 'Failed to update user status');
      }
    });
  }
  unbanOrUnsuspendUser(actionName: 'unban' | 'unsuspend') {
    const userData = this.user();
    if (!userData) return;
    
    // Unban and Unsuspend both just set the user to "active"
    this.adminService.updateUserStatus(
      userData._id,
      true, // isActive
      'active', // accountStatus
      undefined,
      undefined,
      false, // no email
      undefined,
      undefined
    ).subscribe({
      next: res => {
        if (res.auth.success) {
          const updatedUser = this.user();
          if (updatedUser) {
            updatedUser.isActive = true;
            updatedUser.accountStatus = 'active';
            updatedUser.suspendedUntil = undefined;
            updatedUser.suspendedServices = undefined;
            updatedUser.reason = undefined;
          }
          this.toastr.success(`User successfully ${actionName}ned!`);
        } else {
          this.toastr.error(`Failed to ${actionName} user`);
        }
      },
      error: (err) => {
        this.toastr.error(err?.error?.message || `Failed to ${actionName} user`);
      }
    });
  }

  getAvatarColor(): string {
    const colors = [
      "#857031",
      "#857031",
      "#857031",
      "#857031",
      "#857031",
      "#857031",
    ];
    const userData = this.user();
    if (!userData) return colors[0];
    const index = userData._id.charCodeAt(0) % colors.length;
    return colors[index];
  }

  getSubscriptionSeverity(subscriptionType: string): string {
    const severityMap: Record<string, string> = {
      premium: "p-tag-success",
      pro: "p-tag-info",
      basic: "p-tag-secondary",
      free: "p-tag-contrast",
    };

    return severityMap[subscriptionType.toLowerCase()] || "p-tag-secondary";
  }

  // getSubscriptionSeverity(
  //   subscriptionType: string
  // ): "success" | "secondary" | "info" | "warning" | "danger" | "contrast" {
  //   const severityMap: Record<
  //     string,
  //     "success" | "secondary" | "info" | "warning" | "danger" | "contrast"
  //   > = {
  //     premium: "success",
  //     pro: "info",
  //     basic: "secondary",
  //     free: "contrast",
  //   };
  //   return severityMap[subscriptionType.toLowerCase()] || "secondary";
  // }

  getGenderIcon(gender: string): string {
    const iconMap: Record<string, string> = {
      male: "pi pi-mars ml-2",
      female: "pi pi-venus ml-2",
      other: "pi pi-user ml-2",
    };
    return iconMap[gender.toLowerCase()] || "pi pi-user ml-2";
  }

  getAuthProviderIcon(provider: string | undefined): string {
    if (!provider) return 'pi pi-envelope';
    const iconMap: Record<string, string> = {
      google: "pi pi-google",
      apple: "pi pi-apple",
      email: "pi pi-envelope",
    };
    return iconMap[provider.toLowerCase()] || "pi pi-envelope";
  }

  // getEarningsPercentage(): number {
  //   const userData = this.user();
  //   if (!userData) return 0;
  //   if (total === 0) return 0;
  //   return ((userData.totalEarned || 0) / total) * 100;
  // }

}